import { FULFILLMENT_MODE } from "@/constants/fulfillment-mode.js";

/** @enum {string} Customer order delivery intent (creation modal). */
export const ORDER_DELIVERY_MODES = Object.freeze({
    NO_DELIVERY: "no_delivery",
    PICKUP: "pickup",
    SHIPPING: "shipping",
});

/**
 * @param {Record<string, unknown> | null | undefined} order
 */
export function isCustomerOrderRegisterSale(order) {
    if (!order) {
        return false;
    }
    if (order.is_register_sale === true) {
        return true;
    }
    const orderData = order.order_data;
    return !!(orderData && orderData.is_register_sale === true);
}

/**
 * Page title for the customer order detail view.
 * Pickup orders show "{Pickup} - {order_id}"; POS register sales show the id only.
 *
 * @param {Record<string, unknown> | null | undefined} order
 * @param {(key: string) => string} translate
 */
export function getCustomerOrderPageTitle(order, translate) {
    const orderId = order?.order_id || "";
    if (!orderId) {
        return "";
    }
    if (isCustomerOrderRegisterSale(order)) {
        return orderId;
    }
    if (inferCustomerOrderDeliveryMode(order) === ORDER_DELIVERY_MODES.PICKUP) {
        return `${translate("labels.pickup")} - ${orderId}`;
    }
    return orderId;
}

/**
 * @param {Record<string, unknown> | null | undefined} order
 * @param {(key: string) => string} translate
 */
export function getCustomerOrderPageSubtitle(order, translate) {
    if (isCustomerOrderRegisterSale(order)) {
        return translate("registerSale.subtitle");
    }
    return translate("labels.customer_order");
}

/**
 * Vue Router location for opening a customer order (or POS register sale) detail page.
 *
 * @param {Record<string, unknown> | null | undefined} order
 * @returns {{ name: string, params: { customerorder_id: unknown } } | null}
 */
export function customerOrderDetailRoute(order) {
    if (!order?.id) {
        return null;
    }
    return {
        name: isCustomerOrderRegisterSale(order) ? "possale" : "customerorder",
        params: { customerorder_id: order.id },
    };
}

/**
 * @param {Record<string, unknown> | null | undefined} order
 * @returns {string}
 */
export function inferCustomerOrderDeliveryMode(order) {
    if (isCustomerOrderRegisterSale(order)) {
        return ORDER_DELIVERY_MODES.NO_DELIVERY;
    }
    if (order?.fulfillment_mode === FULFILLMENT_MODE.NO_SHIPMENT) {
        return ORDER_DELIVERY_MODES.NO_DELIVERY;
    }
    if (order?.deliverysite) {
        return ORDER_DELIVERY_MODES.SHIPPING;
    }
    if (order?.id) {
        return ORDER_DELIVERY_MODES.PICKUP;
    }
    return ORDER_DELIVERY_MODES.SHIPPING;
}

/**
 * True when the order is shipped to a customer delivery site.
 * @param {Record<string, unknown> | null | undefined} order
 */
export function isCustomerOrderShipping(order) {
    return inferCustomerOrderDeliveryMode(order) === ORDER_DELIVERY_MODES.SHIPPING;
}

/**
 * True when the customer collects goods at a warehouse (no delivery site).
 * @param {Record<string, unknown> | null | undefined} order
 */
export function isCustomerOrderPickup(order) {
    return inferCustomerOrderDeliveryMode(order) === ORDER_DELIVERY_MODES.PICKUP;
}

/**
 * True when the order is configured without customer delivery (logistics UI hidden).
 * @param {Record<string, unknown> | null | undefined} order
 */
export function isCustomerOrderNoDelivery(order) {
    if (!order) {
        return false;
    }
    if (isCustomerOrderRegisterSale(order)) {
        return true;
    }
    return order.fulfillment_mode === FULFILLMENT_MODE.NO_SHIPMENT;
}

/**
 * Whether to open the fulfillment editor after creating an order line so the
 * user can set the departure / logistics plan for that scope.
 *
 * Shown for a newly created (or still unconfigured) scope; skipped when the
 * sale is "no shipment", the product is a service, or the scope already has a
 * fulfillment mode (including no_shipment).
 *
 * @param {{
 *   isNewLine: boolean,
 *   isService?: boolean,
 *   masterOrder: Record<string, unknown> | null | undefined,
 *   scopeOrder: Record<string, unknown> | null | undefined,
 * }} args
 * @returns {boolean}
 */
export function shouldPromptFulfillmentAfterOrderLineCreate({
    isNewLine,
    isService = false,
    masterOrder,
    scopeOrder,
}) {
    if (!isNewLine || isService) {
        return false;
    }
    if (isCustomerOrderNoDelivery(masterOrder)) {
        return false;
    }
    if (!scopeOrder?.id) {
        return false;
    }
    const mode = scopeOrder.fulfillment_mode;
    if (mode != null && mode !== "") {
        return false;
    }
    return true;
}

/**
 * @param {Record<string, unknown> | null | undefined} scope
 */
function scopeShipmentCreationDisabled(scope) {
    if (!scope) {
        return false;
    }
    if (isCustomerOrderNoDelivery(scope)) {
        return true;
    }
    return scope.fulfillment_mode === FULFILLMENT_MODE.NO_SHIPMENT;
}

/**
 * Fulfillment scopes that drive shipment planning (sub-orders on brokerage masters).
 * @param {Record<string, unknown> | null | undefined} order
 * @param {Array<Record<string, unknown>> | null | undefined} [scopeOrders]
 * @returns {Array<Record<string, unknown>>}
 */
export function activeFulfillmentScopes(order, scopeOrders) {
    if (!order) {
        return [];
    }
    const scopes = scopeOrders ?? order.suborders ?? null;
    if (order.suborders_needed) {
        if (!Array.isArray(scopes) || scopes.length === 0) {
            return [];
        }
        return scopes.filter(
            (scope) =>
                scope &&
                !scope.readonly &&
                String(scope.status || "").toLowerCase() !== "canceled"
        );
    }
    return [order];
}

/**
 * True when Safetee must not offer shipment creation for this order scope.
 * On brokerage masters, every active sub-order must be configured without shipment.
 * @param {Record<string, unknown> | null | undefined} order
 * @param {Array<Record<string, unknown>> | null | undefined} [scopeOrders]
 */
export function isCustomerOrderShipmentCreationDisabled(order, scopeOrders) {
    if (!order) {
        return false;
    }
    const scopes = activeFulfillmentScopes(order, scopeOrders);
    if (order.suborders_needed) {
        if (!scopes.length) {
            return scopeShipmentCreationDisabled(order);
        }
        return scopes.every((scope) => scopeShipmentCreationDisabled(scope));
    }
    return scopeShipmentCreationDisabled(order);
}

/**
 * True when at least one active fulfillment scope ships from a warehouse
 * (Safetee outbound shipments), as opposed to no-shipment / vendor delivery note.
 * @param {Record<string, unknown> | null | undefined} order
 * @param {Array<Record<string, unknown>> | null | undefined} [scopeOrders]
 */
export function fulfillmentPlanRequiresOutboundShipments(order, scopeOrders) {
    if (!order) {
        return false;
    }
    const scopes = activeFulfillmentScopes(order, scopeOrders);
    const list = scopes.length ? scopes : [order];
    return list.some((scope) => {
        const mode = scope?.fulfillment_mode;
        return (
            mode === FULFILLMENT_MODE.SHIP_FROM_AGENT_WAREHOUSE ||
            mode === FULFILLMENT_MODE.SHIP_FROM_SUPPLIER_WAREHOUSE
        );
    });
}

/**
 * Remaining shipment work on scopes that still allow shipment creation.
 * @param {Record<string, unknown> | null | undefined} order
 * @param {Array<Record<string, unknown>> | null | undefined} [scopeOrders]
 * @returns {number}
 */
export function customerOrderShipmentToSchedule(order, scopeOrders) {
    if (!order) {
        return 0;
    }
    const scopes = activeFulfillmentScopes(order, scopeOrders);
    if (order.suborders_needed && !scopes.length) {
        return 0;
    }
    return scopes.reduce((total, scope) => {
        if (scopeShipmentCreationDisabled(scope)) {
            return total;
        }
        return total + Number(scope.to_schedule || 0);
    }, 0);
}
